import { useState, useEffect } from "react";

/**
 * Hook to detect if the user is on a touch device (iOS/Android)
 * where native <select> elements provide a better UX than custom dropdowns.
 *
 * Mobile Safari and iOS in general have issues with Radix UI's ScrollArea
 * and custom dropdown implementations. Native <select> elements trigger
 * the native picker wheel which provides a much better touch experience.
 *
 * @returns {Object} { isTouchDevice, isIOS, isAndroid }
 */
export function useTouchDevice() {
  const [deviceInfo, setDeviceInfo] = useState({
    isTouchDevice: false,
    isIOS: false,
    isAndroid: false,
  });

  useEffect(() => {
    if (typeof window === "undefined" || typeof navigator === "undefined") {
      return;
    }

    const userAgent = navigator.userAgent.toLowerCase();

    // Detect iOS (iPhone, iPad, iPod)
    const isIOS =
      /iphone|ipad|ipod/.test(userAgent) ||
      // iPad on iOS 13+ reports as Mac, but has touch
      (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

    // Detect Android
    const isAndroid = /android/.test(userAgent);

    // Check for touch capability
    const hasTouchScreen =
      "ontouchstart" in window ||
      navigator.maxTouchPoints > 0 ||
      // @ts-ignore - msMaxTouchPoints is IE specific
      navigator.msMaxTouchPoints > 0;

    // Consider it a touch device if it's iOS, Android, or has touch + mobile user agent
    const isMobileUserAgent =
      /mobile|android|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(
        userAgent
      );
    const isTouchDevice =
      isIOS || isAndroid || (hasTouchScreen && isMobileUserAgent);

    setDeviceInfo({
      isTouchDevice,
      isIOS,
      isAndroid,
    });
  }, []);

  return deviceInfo;
}

/**
 * Static utility function for non-hook contexts
 * Useful for one-time checks or outside of React components
 */
export function detectTouchDevice() {
  if (typeof window === "undefined" || typeof navigator === "undefined") {
    return { isTouchDevice: false, isIOS: false, isAndroid: false };
  }

  const userAgent = navigator.userAgent.toLowerCase();

  const isIOS =
    /iphone|ipad|ipod/.test(userAgent) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

  const isAndroid = /android/.test(userAgent);

  const hasTouchScreen =
    "ontouchstart" in window ||
    navigator.maxTouchPoints > 0 ||
    // @ts-ignore
    navigator.msMaxTouchPoints > 0;

  const isMobileUserAgent =
    /mobile|android|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(
      userAgent
    );
  const isTouchDevice =
    isIOS || isAndroid || (hasTouchScreen && isMobileUserAgent);

  return { isTouchDevice, isIOS, isAndroid };
}

export default useTouchDevice;
